import { IAngularEvent, IFormController, IScope } from "angular";
import {
  IFieldArray,
  IFieldConfigurationObject,
  IFieldGroup,
  ITemplateOptions,
  ITemplateScope,
  ITemplateScopeOptions,
} from "AngularFormly";
import { cloneDeep, isArray } from "lodash";
import { IRowCommandEventArgs } from "./RowCommandController";

const idSymbol = Symbol("Id");

interface IGameConfigurationControllerScope extends IScope {
  getAllRowFields: () => IFieldArray[];
  getRows: () => any;
  getEditModel: () => any;
  add: (template: any) => void;
  getRowFields: (index: number) => IFieldArray | undefined;
  remove: (model: any) => void;
  copy: (model: any) => void;
  getRecordId: (model: any) => number;
  onKeypress: (event: IAngularEvent, formController: IFormController) => void;
}

export interface IGameConfigurationControllerTemplateOptions
  extends ITemplateOptions {
  readFields: IFieldArray;
  addText: string;
  editFields: IFieldArray;
}

export class GameConfigurationController {
  static $inject = ["$scope"];

  private readonly repeaterModelController: RepeaterModelController;

  constructor(private readonly $scope: IGameConfigurationControllerScope) {
    this.initializeIdsOnRows(this.model);

    this.repeaterModelController = new RepeaterModelController(
      this.model,
      this.options.key ?? "",
      this.templateOptions.readFields,
      this.templateOptions.editFields,
      (model) => this.modelChanged(model)
    );
    this.initializeScope(this.repeaterModelController);
  }

  private modelChanged(newModel: any[]) {
    const model = (this.model ??= []);
    model.splice(0, model.length, ...newModel);
  }

  private initializeIdsOnRows(model: any[] | undefined) {
    model?.forEach((row) => {
      if (!row.Id) {
        row[idSymbol] = --this.currentId;
      }
    });
  }

  private initializeScope(repeaterModelController: RepeaterModelController) {
    this.$scope.add = (template) => {
      const newRow = this.cloneRow(template);
      repeaterModelController.addAtIndex(newRow);
    };

    this.$scope.getRowFields = (index) =>
      repeaterModelController.formlyFormFields[index];

    this.$scope.remove = (model) => {
      const index = this.repeaterModelController.repeaterModel.indexOf(model);
      repeaterModelController.removeAtIndex(index);
    };

    this.$scope.getRecordId = (model) => model[idSymbol];
    this.$scope.getEditModel = () => repeaterModelController.editModel;
    this.$scope.getRows = () => repeaterModelController.repeaterModel;
    this.$scope.getAllRowFields = () =>
      repeaterModelController.formlyFormFields;

    this.$scope.onKeypress = (e: any, formController: IFormController) => {
      if (e.which === 13) {
        e.preventDefault();
        this.repeaterModelController.commitEdit(formController);
      }
    };

    this.$scope.$on("rowCommand", (event, args) =>
      this.onRowCommand(event, args)
    );
  }

  private onRowCommand(event: IAngularEvent, args: IRowCommandEventArgs) {
    const stop = () => event.stopPropagation?.call(this);
    const indexOf = (row: any) =>
      this.repeaterModelController.repeaterModel.indexOf(row) ?? -1;

    switch (args.name) {
      case "submitRowEdit":
        this.repeaterModelController.commitEdit(args.form);
        stop();
        break;
      case "cancelRowEdit":
        this.repeaterModelController.cancelEdit(args.form);
        stop();
        break;
      case "copyRow":
        const template = args.model;
        const newRow = this.cloneRow(template);

        const templateIndex = indexOf(template);
        const copyAtIndex =
          templateIndex > -1 ? templateIndex + 1 : this.model?.length ?? 0;
        this.repeaterModelController.addAtIndex(newRow, copyAtIndex);

        stop();
        break;
      case "editRow":
        const editIndex = indexOf(args.model);
        this.repeaterModelController.editAtIndex(editIndex);
        stop();
        break;
      case "deleteRow":
        const deleteIndex = indexOf(args.model);
        this.repeaterModelController.removeAtIndex(deleteIndex);
        stop();
        break;
    }
  }

  private get options(): ITemplateScopeOptions {
    return this.templateScope.options;
  }

  private get templateScope(): ITemplateScope {
    return <ITemplateScope>(<unknown>this.$scope);
  }

  private get templateOptions(): IGameConfigurationControllerTemplateOptions {
    return <IGameConfigurationControllerTemplateOptions>this.templateScope.to;
  }

  private get model(): any[] | undefined {
    const model = this.templateScope.model ?? {};
    const key = this.options.key ?? "";
    return model[key];
  }

  private set model(value: any[] | undefined) {
    const model = this.templateScope.model ?? {};
    const key = this.templateScope.options.key ?? "";
    model[key] = value;
  }

  private currentId: number = 0;
  private cloneRow(template: any) {
    const clone = cloneDeep(template ?? {});
    delete clone.Id;
    clone[idSymbol] = --this.currentId;

    return clone;
  }
}

class RepeaterModelController {
  private _isNewItem: boolean = false;
  private _editModel: any;
  readonly formlyFormFields: IFieldArray[] = [];
  readonly repeaterModel: any[];

  constructor(
    repeaterModel: any[] | undefined,
    private readonly baseFieldName: string,
    private readonly readFields: IFieldArray,
    private readonly editFields: IFieldArray,
    private readonly onModelChange: (model: any[]) => void
  ) {
    this.repeaterModel = cloneDeep(repeaterModel ?? []);
    this.formlyFormFields = this.getInitialFormlyFields(repeaterModel);
  }

  get editIndex() {
    return this.repeaterModel.indexOf(this._editModel);
  }

  get isNewItem() {
    return this._isNewItem;
  }

  get editModel() {
    return this._editModel;
  }

  removeAtIndex(index: number) {
    if (index < 0 || index >= this.repeaterModel.length) {
      return;
    }

    this.repeaterModel.splice(index, 1);
    this.formlyFormFields.splice(index, 1);

    const editIndex = this.editIndex;

    if (this._isNewItem && index === editIndex) {
      this._editModel = undefined;
      this._isNewItem = false;
    }

    this.onModelChange([...this.repeaterModel]);
  }

  editAtIndex(index: number) {
    if (index < 0 || index >= (this.repeaterModel?.length ?? 0)) {
      return;
    }

    if (this._editModel !== undefined) {
      return;
    }

    const editModel = this.repeaterModel[index];
    this.configureEdit(editModel, false);
  }

  cancelEdit(fc: any) {
    if (this._editModel === undefined) {
      return;
    }

    const repeaterModel = this.repeaterModel;
    const editIndex = this.editIndex;

    if (this._isNewItem) {
      repeaterModel.splice(editIndex, 1);
      this.formlyFormFields.splice(editIndex, 1);
      this._editModel = undefined;
      this._isNewItem = false;
      return;
    }

    const newFormlyFields = this.cloneRowFields(editIndex, this.readFields);
    this.formlyFormFields.splice(editIndex, 1, newFormlyFields);

    this._editModel = undefined;
    this._isNewItem = false;
  }

  addAtIndex(model: any, index?: number) {
    const repeaterModel = this.repeaterModel;
    index ??= repeaterModel.length;

    if (index < 0 || index > repeaterModel.length) {
      return;
    }

    if (this._editModel !== undefined) return;

    this.repeaterModel.splice(index, 0, model);

    const newFormlyFields = this.cloneRowFields(index, this.readFields);
    this.formlyFormFields.splice(index, 0, newFormlyFields);

    this.configureEdit(model, true);
  }

  commitEdit(fc: IFormController) {
    const editIndex = this.editIndex;
    if (editIndex < 0) return;

    fc.$submitted = true;

    if (fc.$valid === false) {
      return;
    }

    const newFormlyFields = this.cloneRowFields(editIndex, this.readFields);
    this.formlyFormFields.splice(editIndex, 1, newFormlyFields);

    this._editModel = undefined;
    this._isNewItem = false;

    this.onModelChange([...this.repeaterModel]);
  }

  private configureEdit(editModel: any, isNewItem: boolean) {
    const index = this.repeaterModel.indexOf(editModel);
    if (editModel === undefined || !this.repeaterModel.includes(editModel)) {
      throw new Error(
        "editModel must be undefined or an object within repeaterModel"
      );
    }

    this._editModel = editModel;
    this._isNewItem = isNewItem ?? false;

    const editFields = this.cloneRowFields(index, this.editFields);
    this.formlyFormFields.splice(index, 1, editFields);
  }

  private getInitialFormlyFields(model: any[] | undefined): IFieldArray[] {
    return (
      model?.map((_, index) => this.cloneRowFields(index, this.readFields)) ??
      []
    );
  }

  private cloneRowFields(index: number, fields: IFieldArray) {
    fields.forEach((field) => {
      const baseFieldName = this.baseFieldName;
      if (baseFieldName === undefined) return;
      this.setFieldNames(field, baseFieldName, index);
    });
    console.log(fields);
    return fields;
  }

  private setFieldNames(
    f: IFieldGroup | IFieldConfigurationObject | IFieldArray,
    baseFieldName: string,
    index: number
  ) {
    for (const field of iterateFieldArray(f)) {
      field.name = `${baseFieldName}[${index}].${field.key}`;
    }
  }
}

function* iterateFieldArray(
  fields: IFieldArray | IFieldConfigurationObject | IFieldGroup
): IterableIterator<IFieldConfigurationObject> {
  const fieldGroup = Object.keys(fields).includes("fieldGroup")
    ? <IFieldGroup>fields
    : undefined;
  const fieldConfigurationObject = Object.keys(fields).includes(
    "templateOptions"
  )
    ? <IFieldConfigurationObject>fields
    : undefined;
  const fieldArray = isArray(fields) ? <IFieldArray>fields : undefined;

  if (fieldGroup !== undefined) {
    const fieldGroupFields: IFieldArray =
      fieldGroup.fieldGroup ?? <IFieldArray>[];
    for (const field of fieldGroupFields) {
      for (const child of iterateFieldArray(field)) {
        yield child;
      }
    }
  } else if (fieldConfigurationObject !== undefined) {
    yield fieldConfigurationObject;
  } else if (fieldArray !== undefined) {
    for (const field of fieldArray) {
      for (const child of iterateFieldArray(field)) {
        yield child;
      }
    }
  }
}